import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";

import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { themevals } from "theme/themevals";
import { getRfqMaintenanceContext } from "api/QuoteRequests";
import { workOrderDisplayIdentity } from "containers/Maintenance/components/workOrderPresentation";

/**
 * A15 — the "part of maintenance plan X / work order Y ← back" banner,
 * shared across every trunk record a Work Order spawns (RFQ, Quote,
 * Invoice, PO) AND the Project (F9 — folded in so the project matches the
 * other records instead of carrying its own rail buttons).
 *
 * It offers a consistent way back to BOTH the work order and (when the
 * context carries a `planId`) the maintenance plan, so an operator who
 * followed a WO out to any of its records returns the same way from all
 * of them.
 *
 * WHY IT EXISTS: the RFQ pricing-verification pivot (OPERATING_MODEL §3
 * Step 3, RFQ_PRICING_VERIFICATION.md) gave the RFQ view a context
 * banner + "Back to work order" link so verifying an RFQ never felt like
 * leaving the plan. The team's finding (A15) is that the SAME return
 * path is needed from the quote, invoice, and PO — an operator who
 * followed a WO out to any of its trunk records needs one consistent way
 * back. Rather than copy the banner markup into four views (the drift
 * this module keeps fighting), the banner is one component the records
 * drop in.
 *
 * DATA: the context is resolved from the RFQ, because the WO↔RFQ link
 * (`work_order.convertedRfqId`) is the anchor the existing
 * `GET /maintenance/work-orders/by-rfq/:rfqId/context` endpoint reads.
 * Every downstream record can name its originating RFQ:
 *   - RFQ:     its own id
 *   - Quote:   quote.quoteRequest.id
 *   - Invoice: invoice.quote.quoteRequest.id  (when the payload carries it)
 *   - PO:      via its project's originQuoteRequest (when available)
 * so this component takes an `rfqId` and self-fetches. It renders NOTHING
 * for a plain (non-WO) record — the endpoint returns null — so it is
 * safe to mount unconditionally on any of these views.
 *
 * STAFF ONLY: the underlying endpoint is staff-scoped and returns null
 * for client viewers, so the banner naturally does not render for them.
 * Callers still gate on `isCompany` where they already do, to avoid the
 * fetch entirely.
 *
 * `showNte` defaults to true (the RFQ verification surface wants the NTE
 * ceiling in view). Downstream records where the NTE is not the point
 * (a posted invoice, say) can pass `showNte={false}`; the ceiling is
 * also simply omitted whenever the context carries no `nteAmount`.
 */
export default function MaintenanceContextBanner({
  rfqId,
  showNte = true,
}: {
  rfqId: string | null | undefined;
  showNte?: boolean;
}) {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState<any>(null);

  useEffect(() => {
    let alive = true;
    if (!rfqId) {
      setCtx(null);
      return;
    }
    (async () => {
      const res = await getRfqMaintenanceContext({ rfqId });
      if (!alive) return;
      setCtx(res && res.workOrderId ? res : null);
    })();
    return () => {
      alive = false;
    };
  }, [rfqId]);

  if (!ctx) return null;

  // A12 — same number-first / name-secondary identity the queue and the
  // case file use, resolved in the one shared place. The context carries
  // `externalId` (the WO number) and `name` (the descriptor); map its
  // `workOrderId` to the `id` the resolver falls back to.
  const identity = workOrderDisplayIdentity({
    externalId: ctx.externalId,
    name: ctx.name,
    id: ctx.workOrderId,
  });

  return (
    <Flex
      sx={{
        backgroundColor: "#F1EFE8",
        border: "1px solid #D3D1C7",
        borderRadius: "8px",
        padding: "14px 20px",
        marginBottom: "24px",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        flexWrap: "wrap",
      }}
    >
      <Box>
        <P
          fontFamily="Poppins"
          color="#000"
          fontSize="13px"
          fontWeight={600}
          marginTop="0px"
          marginBottom="4px"
        >
          {"Work order " + identity.primary}
          {ctx.planName ? " \u00b7 " + ctx.planName : ""}
          {ctx.batchName ? " \u00b7 " + ctx.batchName : ""}
        </P>
        {identity.secondary && (
          <P
            fontFamily="Poppins"
            color={themevals.colors.dkgrey}
            fontSize="11px"
            fontWeight={500}
            marginTop="0px"
            marginBottom="4px"
          >
            {identity.secondary}
          </P>
        )}
        <P
          fontFamily="Poppins"
          color={themevals.colors.dkgrey}
          fontSize="11px"
          fontWeight={400}
          marginTop="0px"
          marginBottom="0px"
        >
          Part of a maintenance plan. Return to the work order to see the full case.
        </P>
      </Box>
      <Flex sx={{ alignItems: "center", gap: "20px", flexShrink: 0 }}>
        {showNte && ctx.nteAmount != null && (
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "#7A5A00" }}>NTE ceiling</Box>
            <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "#7A5A00" }}>
              {Number(ctx.nteAmount).toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </Box>
          </Flex>
        )}
        {ctx.planId && (
          <BasicButton
            scheme="secondary"
            outline={true}
            styles={{ margin: "0" }}
            type="button"
            onClick={() => navigate("/maintenance/plans/" + ctx.planId)}
            keepText={true}
          >
            Back to plan
          </BasicButton>
        )}
        <BasicButton
          scheme="secondary"
          outline={true}
          styles={{ margin: "0" }}
          type="button"
          onClick={() => navigate("/maintenance/work-orders/" + ctx.workOrderId)}
          keepText={true}
        >
          Back to work order
        </BasicButton>
      </Flex>
    </Flex>
  );
}
